import { resolveImageUrl } from "../../lib/api";
import api from "../../lib/api";
import { useState } from "react";
import { Button } from "../ui/button";
import ReviewBadge from "../ReviewBadge";
import ChatPanel from "../ChatPanel";
import LiveTrackingMap from "../LiveTrackingMap";
import TripChronometer from "../TripChronometer";
import CancelBookingDialog from "../CancelBookingDialog";
import { MapPin, CreditCard, CheckCircle, KeyRound, MapPinned, Phone, MessageCircle, Star, Clock, Share2, Wallet, Hourglass, XCircle } from "lucide-react";
const BOOKING_STATUS_LABELS = { pending: "En attente", confirmed: "Confirme", in_progress: "En cours", completed: "Termine", cancelled: "Annule" };
const BOOKING_STATUS_STYLES = { pending: "bg-[#FACC15]/10 text-[#EAB308]", confirmed: "bg-[#10B981]/10 text-[#10B981]", in_progress: "bg-[#3B82F6]/10 text-[#3B82F6]", completed: "bg-[#64748B]/10 text-[#64748B]", cancelled: "bg-[#EF4444]/10 text-[#EF4444]" };

const CITY_COORDS = {
  "yaounde": { lat: 3.848, lng: 11.5021 },
  "douala": { lat: 4.0511, lng: 9.7679 },
  "bafoussam": { lat: 5.4737, lng: 10.4176 },
  "bamenda": { lat: 5.9597, lng: 10.1460 },
  "garoua": { lat: 9.3014, lng: 13.3987 },
  "maroua": { lat: 10.5910, lng: 14.3158 },
  "ngaoundere": { lat: 7.3215, lng: 13.5844 },
  "bertoua": { lat: 4.5773, lng: 13.6846 },
  "ebolowa": { lat: 2.9000, lng: 11.1500 },
  "kribi": { lat: 2.9408, lng: 9.9098 },
  "limbe": { lat: 4.0219, lng: 9.2081 },
  "buea": { lat: 4.1597, lng: 9.2295 },
};

function getCityCoords(name) {
  if (!name) return null;
  const key = name.toLowerCase().trim();
  for (const [city, coords] of Object.entries(CITY_COORDS)) {
    if (key.includes(city) || city.includes(key)) return coords;
  }
  return null;
}

export function PassengerBookingCard({
  booking: b, userId, chatBookingId, setChatBookingId,
  trackingBookingId, driverLocation,
  onShowOtp, onPay, onReview,
  onStartTracking, onStopTracking, onCancelled,
}) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const canCancel = ["pending", "confirmed"].includes(b.status) && !b.otp_verified;

  const handleShareLive = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      // Issue (or refresh) a live-tracking share token bound to this booking.
      const { data } = await api.post(`/bookings/${b.id}/share-token`);
      const trackUrl = `${window.location.origin}/track/${data.token}`;
      const text = [
        `📍 Suivez ma course TrigoTour en direct :`,
        `${trackUrl}`,
        ``,
        `Chauffeur : ${b.driver_name || "N/A"}${b.plate_number ? ` (${b.plate_number})` : ""}`,
        b.departure && b.destination ? `Trajet : ${b.departure} → ${b.destination}` : "",
        `Vous pourrez suivre le véhicule sur la carte en temps réel.`,
      ].filter(Boolean).join("\n");
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) {
      alert(e?.response?.data?.detail || "Impossible de générer le lien de suivi");
    } finally { setSharing(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid={`booking-card-${b.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${b.booking_type === "ride" ? "bg-[#0A2540]/10 text-[#0A2540]" : "bg-[#FACC15]/20 text-[#0A2540]"}`}>
              {b.booking_type === "ride" ? "Covoiturage" : "Location"}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${BOOKING_STATUS_STYLES[b.status] || BOOKING_STATUS_STYLES.pending}`}>
              {BOOKING_STATUS_LABELS[b.status] || b.status}
            </span>
            {b.otp_verified && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#10B981]/10 text-[#10B981]">
                OTP verifie
              </span>
            )}
            {b.payment_method === "tft" && b.tft_debited && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#FACC15]/20 text-[#0A2540] inline-flex items-center gap-1" data-testid={`booking-tft-paid-badge-${b.id}`} title={`Payé via portefeuille TFT${b.tft_cashback_amount ? ` · +${b.tft_cashback_amount} TFT cashback` : ""}`}>
                <Wallet className="w-3 h-3" /> Payé en TFT
              </span>
            )}
            {b.payment_method === "pack" && b.pack_applied && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#10B981]/15 text-[#10B981] inline-flex items-center gap-1" data-testid={`booking-pack-paid-badge-${b.id}`} title={`Couvert par Pack ${b.pack_type} · ${b.pack_hours_used}h débitées`}>
                <Hourglass className="w-3 h-3" /> Pack {b.pack_type}
              </span>
            )}
          </div>
          <p className="text-sm text-[#0A2540] font-medium">{b.departure || b.vehicle_info || "Reservation"} {b.destination ? `-> ${b.destination}` : ""}</p>
          {(b.vehicle_plate || b.vehicle_brand || b.vehicle_color) && (
            <p className="text-[11px] text-[#64748B] flex flex-wrap items-center gap-2 mt-0.5" data-testid={`booking-vehicle-info-${b.id}`}>
              {b.vehicle_brand && <span>{b.vehicle_brand} {b.vehicle_model || ""}</span>}
              {b.vehicle_color && <span className="text-[#94A3B8]">· {b.vehicle_color}</span>}
              {b.vehicle_plate && (
                <span className="font-mono font-semibold text-[#0A2540] bg-[#FACC15]/20 px-1.5 py-0.5 rounded border border-[#FACC15]/40" data-testid={`booking-plate-${b.id}`}>
                  {b.vehicle_plate}
                </span>
              )}
            </p>
          )}
          {(b.pickup_address || b.pickup_neighborhood) && (
            <p className="text-[11px] text-[#0A2540] flex items-start gap-1 mt-1" data-testid={`booking-pickup-address-${b.id}`}>
              <MapPin className="w-3 h-3 text-[#FACC15] mt-0.5 shrink-0" />
              <span>
                <strong>Prise en charge :</strong>
                {b.pickup_neighborhood ? ` ${b.pickup_neighborhood}` : ""}
                {b.pickup_address ? ` · ${b.pickup_address}` : ""}
              </span>
            </p>
          )}
          {(b.start_time || b.end_time) && b.booking_type === "rental" && (
            <p className="text-[11px] text-[#0A2540] flex items-center gap-1 mt-1" data-testid={`booking-times-${b.id}`}>
              <Clock className="w-3 h-3 text-[#FACC15] shrink-0" />
              <span>
                {b.start_time && <><strong>Départ :</strong> {b.start_time}</>}
                {b.start_time && b.end_time && " · "}
                {b.end_time && <><strong>Retour :</strong> {b.end_time}</>}
              </span>
            </p>
          )}
          {b.booking_type === "rental" && (b.start_date || b.end_date) && (
            <p className="text-[11px] text-[#0A2540] flex items-center gap-1 mt-1" data-testid={`booking-dates-${b.id}`}>
              <Clock className="w-3 h-3 text-[#FACC15] shrink-0" />
              <span>
                <strong>Réservation :</strong>{" "}
                {b.start_date || "?"}{b.end_date && b.end_date !== b.start_date ? ` → ${b.end_date}` : ""}
              </span>
            </p>
          )}
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            {/* Driver photo - larger */}
            {b.driver_photo ? (
              <img src={resolveImageUrl(b.driver_photo)} alt={b.driver_name} className="w-12 h-12 rounded-full object-cover border-2 border-[#FACC15]" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#0A2540]/10 flex items-center justify-center border-2 border-[#CBD5E1]"><MapPinned className="w-6 h-6 text-[#64748B]" /></div>
            )}
            <div>
              <span className="font-medium text-[#0A2540] text-sm">Chauffeur: {b.driver_name || "N/A"}</span>
              {b.driver_rating > 0 && <span className="ml-2"><ReviewBadge rating={b.driver_rating} totalReviews={b.driver_total_reviews} /></span>}
              <div className="flex items-center gap-2 mt-0.5">
                {b.driver_phone && (
                  <>
                    <a href={`tel:${b.driver_phone}`} className="flex items-center gap-0.5 text-[#0A2540] underline hover:text-[#FACC15] transition-colors" data-testid={`call-driver-booking-${b.id}`}><Phone className="w-3 h-3" /> {b.driver_phone}</a>
                    <a href={`https://wa.me/${b.driver_phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-0.5 text-[#25D366] hover:text-[#128C7E] transition-colors" data-testid={`whatsapp-driver-${b.id}`}><Share2 className="w-3 h-3" /> WhatsApp</a>
                  </>
                )}
                <span>| {b.created_at?.split("T")[0]}</span>
                {b.rental_mode && <span className="text-[#3B82F6]">| {b.rental_mode === "city_tour" ? "Location ville" : b.rental_mode === "hourly" ? `Tour ${b.rental_hours || 1}h` : "Sortie de ville"}</span>}
              </div>
            </div>
          </div>
          <TripChronometer booking={b} role="passenger" compact />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {b.otp_code && (b.payment_status === "paid" || b.payment_status === "reserved") && !b.otp_verified && (
            <button onClick={() => onShowOtp(b)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A2540] text-white" data-testid={`show-otp-${b.id}`}>
              <KeyRound className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold">Code OTP</span>
            </button>
          )}
          <Button size="sm" variant="outline" onClick={() => setChatBookingId(chatBookingId === b.id ? null : b.id)} className="border-[#CBD5E1] text-[#0A2540]" data-testid={`chat-toggle-${b.id}`}>
            <MessageCircle className="w-3.5 h-3.5 mr-1" /> {chatBookingId === b.id ? "Fermer" : "Message"}
          </Button>
          <p className="text-lg font-bold text-[#0A2540]">{b.total_amount?.toLocaleString()} XAF</p>
          {b.payment_status !== "paid" && b.payment_status !== "reserved" && (
            <Button size="sm" onClick={() => onPay(b)} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid={`pay-booking-${b.id}`}>
              <CreditCard className="w-3 h-3 mr-1" /> Reserver (Cash)
            </Button>
          )}
          {b.payment_status === "reserved" && (
            <span className="text-xs text-[#3B82F6] font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" /> Reserve - En attente de confirmation
            </span>
          )}
          {b.payment_status === "paid" && (
            <span className="text-xs text-[#10B981] font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Paye
            </span>
          )}
          {/* WhatsApp live location share - only after OTP verified */}
          {b.otp_verified && b.status !== "completed" && b.status !== "cancelled" && (
            <Button size="sm" variant="outline" onClick={handleShareLive} disabled={sharing} className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10" data-testid={`share-location-${b.id}`}>
              <Share2 className="w-3 h-3 mr-1" /> {sharing ? "Génération…" : "Partager ma position"}
            </Button>
          )}
          {b.status === "completed" && !b.has_reviewed && b.driver_id && (
            <Button size="sm" onClick={() => onReview(b)} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid={`review-driver-${b.id}`}>
              <Star className="w-3 h-3 mr-1" /> Noter le chauffeur
            </Button>
          )}
          {b.status === "completed" && b.has_reviewed && (
            <span className="text-xs text-[#64748B] flex items-center gap-1">
              <Star className="w-3 h-3 fill-[#FACC15] text-[#FACC15]" /> Note envoyee
            </span>
          )}
          {canCancel && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCancelOpen(true)}
              className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
              data-testid={`cancel-booking-btn-${b.id}`}
            >
              <XCircle className="w-3 h-3 mr-1" /> Annuler
            </Button>
          )}
        </div>
      </div>
      {b.status === "cancelled" && b.cancellation_reason_label && (
        <div className="mt-2 flex items-start gap-2 text-xs text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/20 rounded-lg px-3 py-2" data-testid={`cancel-reason-${b.id}`}>
          <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <span className="font-bold">Motif : </span>{b.cancellation_reason_label}
            {b.cancellation_reason_details && (
              <span className="block mt-0.5 text-[#7F1D1D] italic">« {b.cancellation_reason_details} »</span>
            )}
          </div>
        </div>
      )}
      {b.pickup_location && (
        <div className="mt-2 flex items-center gap-1 text-xs text-[#10B981]">
          <MapPin className="w-3 h-3" /> Prise en charge: {b.pickup_location}
        </div>
      )}
      {chatBookingId === b.id && (
        <div className="mt-3 border-t border-[#CBD5E1] pt-3">
          <ChatPanel bookingId={b.id} currentUserId={userId} otherUserName={b.driver_name} onClose={() => setChatBookingId(null)} />
        </div>
      )}
      {b.status === "in_progress" && b.otp_verified && (
        <div className="mt-3 border-t border-[#CBD5E1] pt-3">
          {trackingBookingId === b.id ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                  <span className="text-xs font-semibold text-[#0A2540]">Suivi en temps reel</span>
                  {driverLocation?.driver_name && <span className="text-xs text-[#64748B]">- {driverLocation.driver_name}</span>}
                </div>
                <Button size="sm" variant="ghost" onClick={onStopTracking} className="text-xs h-7 text-[#64748B]" data-testid={`stop-live-tracking-${b.id}`}>
                  Fermer la carte
                </Button>
              </div>
              <LiveTrackingMap
                bookingId={b.id}
                driverLocation={driverLocation}
                departure={b.departure || b.pickup_location}
                destination={b.destination || b.dropoff_location}
                departureCoords={getCityCoords(b.departure || b.pickup_location)}
                destinationCoords={getCityCoords(b.destination || b.dropoff_location)}
                height="300px"
              />
            </div>
          ) : (
            <Button size="sm" onClick={() => onStartTracking(b.id)} className="bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid={`start-live-tracking-${b.id}`}>
              <MapPinned className="w-3.5 h-3.5 mr-1.5" /> Suivre mon chauffeur en direct
            </Button>
          )}
        </div>
      )}
      <CancelBookingDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        bookingId={b.id}
        onCancelled={onCancelled}
      />
    </div>
  );
}
